import Route from '@ember/routing/route';
import { service } from '@ember/service';
import ENV from 'tio-employee/config/environment';
import { addError, initDatadog } from 'tio-common/utils/datadog';
import { setSeveredLinkSink } from 'tio-data/utils/severed-link-report';
import type PreRegisterService from '../services/pre-register';
import type SessionService from '../services/session';
import type ZendeskService from '../services/zendesk';
import type FeaturesService from 'tio-common/services/features';
import type TelemetryService from 'tio-common/services/telemetry';
import type { IntlService } from 'ember-intl';
import type RouterService from '@ember/routing/router-service';
import type PartnerService from 'tio-employee/services/partner';
import type FirebaseAnalyticsService from '../services/firebase-analytics';
import type DemoModeService from 'tio-employee/services/demo-mode';
import type BetaExperienceService from 'tio-employee/services/beta-experience';
import type PhoneCaptureService from 'tio-employee/services/phone-capture';
import { loadTranslationsWithFallback } from 'tio-common/utils/load-translations';
import { deepMerge } from 'tio-common/utils/deep-merge';

// Supported locales for tio-employee
const SUPPORTED_LOCALES = ['en-us', 'fr-ca', 'es-mx'] as const;
type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
const DEFAULT_LOCALE: SupportedLocale = 'en-us';

// Map browser language codes to our supported locales
const BROWSER_LOCALE_MAP: Record<string, SupportedLocale> = {
  // French variants -> Quebec French
  fr: 'fr-ca',
  'fr-ca': 'fr-ca',
  'fr-fr': 'fr-ca',
  // Spanish variants -> Mexican Spanish
  es: 'es-mx',
  'es-mx': 'es-mx',
  'es-es': 'es-mx',
  'es-419': 'es-mx', // Latin American Spanish
  // English variants -> US English
  en: 'en-us',
  'en-us': 'en-us',
  'en-gb': 'en-us',
  'en-ca': 'en-us',
};

type TranslationModule = { default?: Record<string, unknown> };

function loadTranslationsFromImports(imports: Array<Promise<TranslationModule>>) {
  return Promise.all(imports).then((modules) => {
    // Use deep merge to properly combine nested keys like pslf.* from multiple files
    const merged = deepMerge({}, ...modules.map((m) => m.default || {}));

    return { default: merged };
  });
}

// Static import map for translations
const APP_TRANSLATIONS = {
  'en-us': () =>
    loadTranslationsFromImports([
      import('../../translations/en-us/general.yaml'),
      import('../../translations/en-us/profile.yaml'),
      import('../../translations/en-us/dashboard.yaml'),
      import('../../translations/en-us/education.yaml'),
      import('../../translations/en-us/tuition-assistance.yaml'),
      import('../../translations/en-us/student-loans.yaml'),
      import('../../translations/en-us/accounts-loans.yaml'),
      import('../../translations/en-us/special-programs.yaml'),
      import('../../translations/en-us/wellness.yaml'),
      import('../../translations/en-us/instructions.yaml'),
      import('../../translations/en-us/glossary.yaml'),
      import('../../translations/en-us/impersonation.yaml'),
      import('../../translations/en-us/forgiveness-estimator-0726.yaml'),
      import('../../translations/en-us/idr-dashboard-0726.yaml'),
      import('../../translations/en-us/unsubscribe.yaml'),
    ]),
  'fr-ca': () =>
    loadTranslationsFromImports([
      import('../../translations/fr-ca/general.yaml'),
      import('../../translations/fr-ca/profile.yaml'),
      import('../../translations/fr-ca/dashboard.yaml'),
      import('../../translations/fr-ca/education.yaml'),
      import('../../translations/fr-ca/tuition-assistance.yaml'),
      import('../../translations/fr-ca/student-loans.yaml'),
      import('../../translations/fr-ca/accounts-loans.yaml'),
      import('../../translations/fr-ca/special-programs.yaml'),
      import('../../translations/fr-ca/wellness.yaml'),
      import('../../translations/fr-ca/instructions.yaml'),
      import('../../translations/fr-ca/glossary.yaml'),
      import('../../translations/fr-ca/impersonation.yaml'),
      import('../../translations/fr-ca/forgiveness-estimator-0726.yaml'),
      import('../../translations/fr-ca/idr-dashboard-0726.yaml'),
      import('../../translations/fr-ca/unsubscribe.yaml'),
    ]),
  'es-mx': () =>
    loadTranslationsFromImports([
      import('../../translations/es-mx/general.yaml'),
      import('../../translations/es-mx/profile.yaml'),
      import('../../translations/es-mx/dashboard.yaml'),
      import('../../translations/es-mx/education.yaml'),
      import('../../translations/es-mx/tuition-assistance.yaml'),
      import('../../translations/es-mx/student-loans.yaml'),
      import('../../translations/es-mx/accounts-loans.yaml'),
      import('../../translations/es-mx/special-programs.yaml'),
      import('../../translations/es-mx/wellness.yaml'),
      import('../../translations/es-mx/instructions.yaml'),
      import('../../translations/es-mx/glossary.yaml'),
      import('../../translations/es-mx/impersonation.yaml'),
      import('../../translations/es-mx/forgiveness-estimator-0726.yaml'),
      import('../../translations/es-mx/idr-dashboard-0726.yaml'),
      import('../../translations/es-mx/unsubscribe.yaml'),
    ]),
} as const;

const COMMON_TRANSLATIONS = {
  'en-us': () => import('../../../addons/tio-common/translations/en-us.yaml'),
  'fr-ca': () => import('../../../addons/tio-common/translations/fr-ca.yaml'),
  'es-mx': () => import('../../../addons/tio-common/translations/es-mx.yaml'),
} as const;

/**
 * Detects the browser's preferred language and maps it to a supported locale.
 */
function detectBrowserLocale(): SupportedLocale | undefined {
  const browserLanguages = navigator.languages || [navigator.language];

  for (const lang of browserLanguages) {
    const normalized = lang.toLowerCase();

    // Direct match
    if (BROWSER_LOCALE_MAP[normalized]) {
      return BROWSER_LOCALE_MAP[normalized];
    }

    // Try base language (e.g., 'fr-BE' -> 'fr')
    const baseLang = normalized.split('-')[0];
    if (baseLang && BROWSER_LOCALE_MAP[baseLang]) {
      return BROWSER_LOCALE_MAP[baseLang];
    }
  }

  return undefined;
}

/**
 * Gets the user's preferred locale with this priority:
 * 1. localStorage override (user explicitly chose a language)
 * 2. Browser language detection
 * 3. Default locale (en-us)
 */
function getPreferredLocale(): SupportedLocale {
  // Check for explicit user preference in localStorage
  const saved = localStorage.getItem('locale');
  if (saved && SUPPORTED_LOCALES.includes(saved as SupportedLocale)) {
    return saved as SupportedLocale;
  }

  // Auto-detect from browser
  const detected = detectBrowserLocale();
  if (detected) {
    return detected;
  }

  // Fall back to default
  return DEFAULT_LOCALE;
}

export default class ApplicationRoute extends Route {
  @service declare intl: IntlService;
  @service declare session: SessionService;
  @service declare preRegister: PreRegisterService;
  @service declare zendesk: ZendeskService;
  @service declare features: FeaturesService;
  @service declare router: RouterService;
  @service declare partner: PartnerService;
  @service declare firebaseAnalytics: FirebaseAnalyticsService;
  @service declare telemetry: TelemetryService;
  @service declare demoMode: DemoModeService;
  @service declare betaExperience: BetaExperienceService;
  @service declare phoneCapture: PhoneCaptureService;

  async beforeModel() {
    // @ts-expect-error: @Julia we need a strategy for unifying more of the ENV
    this.features.initWithEnvironment(ENV);

    // Register capability sources so `features.can(...)` reflects demo + beta +
    // entitlement state with precedence demo > beta > entitlement > flag > default.
    this.features.registerCapabilitySource(this.demoMode.asCapabilitySource());
    this.features.registerCapabilitySource(this.betaExperience.asCapabilitySource());
    this.features.registerCapabilitySource(this.phoneCapture.asCapabilitySource());
    await this.session.setup();

    const locale = getPreferredLocale();

    await loadTranslationsWithFallback({
      intl: this.intl,
      locale,
      fallbackLocale: DEFAULT_LOCALE,
      appTranslationsImport: APP_TRANSLATIONS[locale](),
      commonTranslationsImport: COMMON_TRANSLATIONS[locale](),
      fallbackAppTranslationsImport: APP_TRANSLATIONS[DEFAULT_LOCALE](),
      fallbackCommonTranslationsImport: COMMON_TRANSLATIONS[DEFAULT_LOCALE](),
    });
    this.intl.setLocale(locale);

    if (ENV.datadog) {
      try {
        await initDatadog({
          ...ENV.datadog,
        });
      } catch (error) {
        // Optional integration — a failed dynamic import or runtime SDK
        // error must not block app boot. The wrapper's helper functions
        // (addError, setUser, etc.) already no-op when init didn't run.
        console.warn('Datadog RUM init failed; continuing without it', error);
      }
    }

    // Initialize custom telemetry alongside DD RUM (parallel run)
    if (ENV.telemetry) {
      this.telemetry.configure({
        ...ENV.telemetry,
        app: 'tio-employee',
      });
    }

    // Severed-link observations from tio-data getters land in both reporters; tio-data
    // cannot import either directly (dependency direction), so the sink is injected here.
    setSeveredLinkSink((error, context) => {
      addError(error, { ...context });
      this.telemetry.trackError(error, { ...context });
    });

    this.firebaseAnalytics.initialize();
  }

  model() {
    return this.preRegister.load();
  }

  afterModel() {
    this.zendesk.zendeskAction('messenger', 'hide');
  }

  // Keep per-URL scroll positions
  private scrollPositions = new Map<string, { x: number; y: number }>();

  // Bind handlers so we can unregister
  private handleWillChange = () => {
    const url = this.router.currentURL;
    if (url) {
      this.scrollPositions.set(url, { x: window.scrollX, y: window.scrollY });
    }
  };

  private handleDidChange = () => {
    const url = this.router.currentURL;
    const saved = url ? this.scrollPositions.get(url) : undefined;

    const routeName = this.router.currentRouteName;
    if (routeName) {
      this.telemetry.trackRouteChange(routeName);
    }

    // Wait one frame so the new route has rendered before scrolling
    requestAnimationFrame(() => {
      if (saved) {
        window.scrollTo(saved.x, saved.y); // back/forward: restore
      } else {
        window.scrollTo(0, 0); // fresh nav: go to top
      }
    });
  };

  activate() {
    // Prevent the browser from doing its own restoration that can fight us
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    this.router.on('routeWillChange', this.handleWillChange);
    this.router.on('routeDidChange', this.handleDidChange);
  }

  willDestroy() {
    try {
      this.router.off('routeWillChange', this.handleWillChange);
      this.router.off('routeDidChange', this.handleDidChange);
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'auto';
      }
    } catch (error) {
      // This triggers in the test runner.
      console.error('Error during willDestroy:', error);
    }
  }
}
